import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';

import { AuthenticationService } from './authentication.service';

/** Tempo mínimo com a aba/janela oculta para, ao voltar, sincronizar usuário e saldo com o servidor. */
export const PAGE_VISIBILITY_ABSENCE_THRESHOLD_MS = 5 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class PageVisibilityRefreshService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly ngZone = inject(NgZone);
  private readonly auth = inject(AuthenticationService);
  private readonly destroyRef = inject(DestroyRef);

  private hiddenAt: number | null = null;

  constructor() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      fromEvent(document, 'visibilitychange')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          if (document.visibilityState === 'hidden') {
            this.hiddenAt = Date.now();
            return;
          }
          if (document.visibilityState !== 'visible') {
            return;
          }
          const startHidden = this.hiddenAt;
          this.hiddenAt = null;
          if (startHidden === null) {
            return;
          }
          if (Date.now() - startHidden < PAGE_VISIBILITY_ABSENCE_THRESHOLD_MS) {
            return;
          }
          this.ngZone.run(() => this.refreshAfterLongAbsence());
        });
    });
  }

  private refreshAfterLongAbsence(): void {
    if (!this.auth.isLoggedIn()) {
      return;
    }
    this.auth.getMe().subscribe({
      next: () => {
        this.auth.refreshBalance();
      },
      error: () => {
        // Limpa sessão como em `UserStore.setUser(null)` — feito via `setCurrentUser`, que atualiza o store, IndexedDB e integrações.
        this.auth.setCurrentUser(null);
      },
    });
  }
}
